/**
 * Sistema de Registro de Caídas del Sistema
 * Captura errores 5XX, los almacena y envía reportes automáticos a soporte
 */

import axios from 'axios';

const STORAGE_KEY = 'iba_system_outages';
const MAX_LOCAL_RECORDS = 100;
const REPORT_COOLDOWN_KEY = 'iba_last_outage_report';
const REPORT_COOLDOWN_MS = 300000; // 5 minutos entre reportes para evitar spam (aumentado de 1 min)
const CIRCUIT_BREAKER_KEY = 'iba_outage_circuit_breaker';
const CIRCUIT_BREAKER_THRESHOLD = 3; // Después de 3 fallos, abrir circuito
const CIRCUIT_BREAKER_RESET_MS = 600000; // 10 minutos para resetear el circuito

// Endpoints que NO deben registrar caídas (para evitar loops)
const EXCLUDED_ENDPOINTS = [
  '/api/system/report-outage',
  '/api/system/outages',
  '/api/admin/outages'
];

// Obtener la URL del API
const getApiUrl = () => {
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

// Circuit Breaker - evita llamadas repetidas cuando el servidor está caído
const getCircuitBreaker = () => {
  try {
    const data = localStorage.getItem(CIRCUIT_BREAKER_KEY);
    if (!data) return { failures: 0, lastFailure: 0, isOpen: false };
    return JSON.parse(data);
  } catch {
    return { failures: 0, lastFailure: 0, isOpen: false };
  }
};

const updateCircuitBreaker = (success) => {
  const cb = getCircuitBreaker();
  const now = Date.now();
  
  // Si el circuito está abierto, verificar si debe resetearse
  if (cb.isOpen && (now - cb.lastFailure) > CIRCUIT_BREAKER_RESET_MS) {
    // Resetear circuito después del tiempo de espera
    localStorage.setItem(CIRCUIT_BREAKER_KEY, JSON.stringify({
      failures: 0,
      lastFailure: 0,
      isOpen: false
    }));
    return;
  }
  
  if (success) {
    // Éxito - resetear contador
    localStorage.setItem(CIRCUIT_BREAKER_KEY, JSON.stringify({
      failures: 0,
      lastFailure: 0,
      isOpen: false
    }));
  } else {
    // Fallo - incrementar contador
    const newFailures = cb.failures + 1;
    const isOpen = newFailures >= CIRCUIT_BREAKER_THRESHOLD;
    
    if (isOpen) {
      console.warn(`🔴 Circuit breaker ABIERTO después de ${newFailures} fallos. No se enviarán reportes por ${CIRCUIT_BREAKER_RESET_MS/60000} minutos.`);
    }
    
    localStorage.setItem(CIRCUIT_BREAKER_KEY, JSON.stringify({
      failures: newFailures,
      lastFailure: now,
      isOpen
    }));
  }
};

const isCircuitOpen = () => {
  const cb = getCircuitBreaker();
  
  // Si está abierto, verificar si ya pasó el tiempo de reset
  if (cb.isOpen) {
    const now = Date.now();
    if ((now - cb.lastFailure) > CIRCUIT_BREAKER_RESET_MS) {
      // Resetear y permitir
      updateCircuitBreaker(true);
      return false;
    }
    return true;
  }
  return false;
};

// Obtener registros de caídas desde localStorage
export const getLocalOutages = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading outages from localStorage:', error);
    return [];
  }
};

// Guardar registros de caídas en localStorage
export const saveLocalOutages = (outages) => {
  try {
    const trimmed = outages.slice(-MAX_LOCAL_RECORDS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error('Error saving outages to localStorage:', error);
  }
};

// Verificar si podemos enviar un reporte (cooldown)
const canSendReport = () => {
  try {
    const lastReport = localStorage.getItem(REPORT_COOLDOWN_KEY);
    if (!lastReport) return true;
    
    const lastTime = parseInt(lastReport, 10);
    return (Date.now() - lastTime) > REPORT_COOLDOWN_MS;
  } catch {
    return true;
  }
};

// Marcar que enviamos un reporte
const markReportSent = () => {
  localStorage.setItem(REPORT_COOLDOWN_KEY, Date.now().toString());
};

// Enviar reporte automático a soporte
const sendOutageReport = async (outage) => {
  // Verificar si el endpoint del error es uno excluido (evitar loops)
  const isExcludedEndpoint = EXCLUDED_ENDPOINTS.some(excluded => 
    outage.endpoint?.includes(excluded)
  );
  
  if (isExcludedEndpoint) {
    console.log('⏭️ Endpoint excluido, no se reporta para evitar loops:', outage.endpoint);
    return { success: false, reason: 'excluded_endpoint' };
  }
  
  // Verificar circuit breaker
  if (isCircuitOpen()) {
    console.log('🔴 Circuit breaker abierto, saltando envío de reporte');
    return { success: false, reason: 'circuit_open' };
  }
  
  // Verificar cooldown para no enviar muchos emails seguidos
  if (!canSendReport()) {
    console.log('⏳ Reporte en cooldown, esperando antes de enviar otro...');
    return { success: false, reason: 'cooldown' };
  }

  try {
    const API = getApiUrl();
    const token = localStorage.getItem('token');
    
    const reportData = {
      errorCode: String(outage.errorCode),
      endpoint: outage.endpoint,
      method: outage.method || 'GET',
      message: outage.message || 'Error de servidor',
      userEmail: outage.userEmail || 'No logueado',
      timestamp: outage.timestamp,
      timestampLocal: outage.timestampLocal,
      userAgent: navigator.userAgent,
      outageId: outage.id
    };

    // Intentar enviar el reporte con timeout corto
    const response = await axios.post(
      `${API}/api/system/report-outage`,
      reportData,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 5000 // 5 segundos timeout (reducido de 10)
      }
    );

    if (response.data.success) {
      console.log('✅ Reporte de caída enviado a soporte automáticamente');
      markReportSent();
      updateCircuitBreaker(true); // Éxito - resetear circuit breaker
      return { success: true };
    } else {
      console.warn('⚠️ Reporte enviado pero con advertencia:', response.data);
      return { success: false, error: response.data.error };
    }
  } catch (error) {
    // Si falla el envío del reporte, actualizar circuit breaker
    updateCircuitBreaker(false);
    console.error('❌ No se pudo enviar reporte a soporte:', error.message);
    return { success: false, error: error.message };
  }
};

// Registrar una nueva caída
export const recordOutage = async (errorData) => {
  // IMPORTANTE: No registrar errores del propio sistema de reportes (evita loops)
  const endpoint = errorData.url || errorData.endpoint || 'Unknown';
  const isExcludedEndpoint = EXCLUDED_ENDPOINTS.some(excluded => 
    endpoint.includes(excluded)
  );
  
  if (isExcludedEndpoint) {
    console.log('⏭️ No se registra caída de endpoint excluido:', endpoint);
    return null;
  }
  
  const outage = {
    id: `outage_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    timestampLocal: new Date().toLocaleString('es-UY', { timeZone: 'America/Montevideo' }),
    errorCode: errorData.status || errorData.code || 'UNKNOWN',
    endpoint: endpoint,
    method: errorData.method || 'GET',
    message: errorData.message || 'Error de servidor',
    userEmail: errorData.userEmail || localStorage.getItem('userEmail') || 'No logueado',
    userAgent: navigator.userAgent,
    synced: false,
    reportSent: false
  };

  const outages = getLocalOutages();
  outages.push(outage);
  saveLocalOutages(outages);

  console.warn('📉 Caída registrada:', outage);

  // Intentar enviar reporte automático a soporte (con protecciones)
  const reportResult = await sendOutageReport(outage);
  
  if (reportResult.success) {
    // Marcar como reportado
    outage.reportSent = true;
    outage.reportSentAt = new Date().toISOString();
    const updatedOutages = getLocalOutages();
    const idx = updatedOutages.findIndex(o => o.id === outage.id);
    if (idx !== -1) {
      updatedOutages[idx] = outage;
      saveLocalOutages(updatedOutages);
    }
  }

  return outage;
};

// Marcar caídas como sincronizadas
export const markOutagesSynced = (outageIds) => {
  const outages = getLocalOutages();
  const updated = outages.map(o => 
    outageIds.includes(o.id) ? { ...o, synced: true } : o
  );
  saveLocalOutages(updated);
};

// Obtener caídas no sincronizadas
export const getUnsyncedOutages = () => {
  return getLocalOutages().filter(o => !o.synced);
};

// Obtener caídas no reportadas
export const getUnreportedOutages = () => {
  return getLocalOutages().filter(o => !o.reportSent);
};

// Reintentar envío de reportes pendientes
export const retryPendingReports = async () => {
  const unreported = getUnreportedOutages();
  console.log(`📤 Reintentando envío de ${unreported.length} reportes pendientes...`);
  
  for (const outage of unreported.slice(0, 5)) { // Máximo 5 a la vez
    const result = await sendOutageReport(outage);
    if (result.success) {
      const outages = getLocalOutages();
      const idx = outages.findIndex(o => o.id === outage.id);
      if (idx !== -1) {
        outages[idx].reportSent = true;
        outages[idx].reportSentAt = new Date().toISOString();
        saveLocalOutages(outages);
      }
    }
    // Esperar un poco entre envíos
    await new Promise(resolve => setTimeout(resolve, 2000));
  }
};

// Limpiar registros antiguos (más de 30 días)
export const cleanOldOutages = () => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const outages = getLocalOutages();
  const filtered = outages.filter(o => new Date(o.timestamp) > thirtyDaysAgo);
  saveLocalOutages(filtered);
  
  return outages.length - filtered.length;
};

// Exportar registros a JSON
export const exportOutagesToJSON = () => {
  const outages = getLocalOutages();
  const dataStr = JSON.stringify(outages, null, 2);
  const blob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `caidas_sistema_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Exportar registros a CSV
export const exportOutagesToCSV = () => {
  const outages = getLocalOutages();
  
  const headers = ['Fecha/Hora', 'Código Error', 'Endpoint', 'Método', 'Mensaje', 'Usuario', 'Reportado', 'Sincronizado'];
  const rows = outages.map(o => [
    o.timestampLocal || o.timestamp,
    o.errorCode,
    o.endpoint,
    o.method,
    `"${(o.message || '').replace(/"/g, '""')}"`,
    o.userEmail,
    o.reportSent ? 'Sí' : 'No',
    o.synced ? 'Sí' : 'No'
  ]);

  const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `caidas_sistema_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Estadísticas de caídas
export const getOutageStats = () => {
  const outages = getLocalOutages();
  
  if (outages.length === 0) {
    return {
      total: 0,
      today: 0,
      lastWeek: 0,
      lastMonth: 0,
      reported: 0,
      pending: 0,
      byErrorCode: {},
      byEndpoint: {},
      lastOutage: null
    };
  }

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const byErrorCode = {};
  const byEndpoint = {};

  outages.forEach(o => {
    byErrorCode[o.errorCode] = (byErrorCode[o.errorCode] || 0) + 1;
    const endpointSimple = o.endpoint.split('?')[0].split('/').slice(0, 4).join('/');
    byEndpoint[endpointSimple] = (byEndpoint[endpointSimple] || 0) + 1;
  });

  return {
    total: outages.length,
    today: outages.filter(o => new Date(o.timestamp) >= todayStart).length,
    lastWeek: outages.filter(o => new Date(o.timestamp) >= weekAgo).length,
    lastMonth: outages.filter(o => new Date(o.timestamp) >= monthAgo).length,
    reported: outages.filter(o => o.reportSent).length,
    pending: outages.filter(o => !o.reportSent).length,
    byErrorCode,
    byEndpoint,
    lastOutage: outages[outages.length - 1]
  };
};

// Limpiar todos los registros
export const clearAllOutages = () => {
  localStorage.removeItem(STORAGE_KEY);
};

export default {
  recordOutage,
  getLocalOutages,
  getOutageStats,
  exportOutagesToJSON,
  exportOutagesToCSV,
  cleanOldOutages,
  clearAllOutages,
  getUnsyncedOutages,
  markOutagesSynced,
  getUnreportedOutages,
  retryPendingReports
};
